import { Link } from 'react-router-dom';
import {
  HardHat, ArrowLeft, Shield, Scale, Users, HeartHandshake,
  Lightbulb, Eye, CheckCircle, ArrowRight
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const VALUES = [
  {
    icon: Eye,
    title: 'Radical transparency',
    body:
      'You always know what you\'re paying, who you\'re talking to, and how a decision was made. No hidden lead-fees, no surprise charges, no opaque rankings. Every pro\'s license, insurance, and review history is verifiable before you commit.',
  },
  {
    icon: Scale,
    title: 'Honest by default',
    body:
      'We don\'t stage reviews, we don\'t sell leads to unverified pros, and we don\'t pretend to be what we\'re not. ContractorsLab is a marketplace — not a contractor, not an escrow service, not a guarantor. We\'re upfront about what we do and what we leave to the professionals on each side.',
  },
  {
    icon: HeartHandshake,
    title: 'Performance before promises',
    body:
      'Contractors pay only when a client actually chooses them. That alignment keeps our incentives honest — we succeed only when a real project gets matched to the right pro.',
  },
];

const DIVERSITY = [
  'We welcome contractors and clients of every race, ethnicity, color, national origin, ancestry, sex, gender, gender identity, sexual orientation, marital status, age, religion, disability, veteran status, or any other protected characteristic under federal, state, or local law.',
  'Matching, lead routing, and verification decisions are based on license status, insurance, service area, and client-specific needs — never on identity.',
  'We do not tolerate discrimination, harassment, or retaliation on the platform. Violations are grounds for immediate account termination under our Terms of Service.',
  'We actively invest in outreach to women-owned, minority-owned, veteran-owned, and small-business contractor groups — because the trades are stronger when every qualified pro has the same shot at every job.',
];

const AboutPage = () => (
  <div className="min-h-screen bg-slate-50">
    <SEO
      title="About ContractorsLab — Honesty, Transparency, and Diversity"
      description="ContractorsLab is a performance-based contractor marketplace created and operated by Retention Logic LLC. Our mission, values, and commitment to transparency and diversity."
      keywords="ContractorsLab about, Retention Logic LLC, contractor marketplace mission, diversity statement"
      path="/about"
    />

    {/* Nav */}
    <nav className="bg-slate-900 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="about-home-link">
            <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="dark" />
            <Link to="/" className="text-slate-300 hover:text-white font-medium text-sm flex items-center gap-1" data-testid="about-back-link">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b-4 border-orange-600">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <span className="text-orange-400 font-bold uppercase tracking-wider text-sm">About Us</span>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[0.95]"
          data-testid="about-title"
        >
          Built on honesty.<br />
          Priced on performance.<br />
          <span className="text-orange-500">Open to everyone.</span>
        </h1>
        <p className="text-slate-300 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
          ContractorsLab.com is a performance-based marketplace that connects homeowners and property
          managers with <strong className="text-white">licensed, insured, verified</strong> contractors —
          without lead-fees, paid placement, or hidden algorithms.
        </p>
      </div>
    </section>

    {/* Who we are */}
    <section className="max-w-4xl mx-auto px-4 md:px-8 py-14">
      <div className="prose prose-slate max-w-none">
        <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">Who we are</h2>
        <p className="text-slate-700 text-base md:text-lg leading-relaxed">
          ContractorsLab.com is <strong>created, owned, and operated by Retention Logic LLC</strong>,
          a limited liability company organized under the laws of the <strong>Commonwealth of Puerto
          Rico</strong>. We're a small, hands-on team of engineers, operators, and tradespeople who
          were tired of watching homeowners get ripped off by unverified contractors — and tired of
          watching honest contractors waste money on pay-per-lead platforms that reward noise over
          quality.
        </p>
        <p className="text-slate-700 text-base md:text-lg leading-relaxed mt-4">
          We built ContractorsLab to flip that model: free to post, free to browse, free to bid.
          Contractors pay only when a client actually chooses them. No middleman drama, no lead games,
          no inflated subscription tiers. Just the right pro for the right job.
        </p>
      </div>
    </section>

    {/* Mission */}
    <section className="bg-white border-y border-slate-200">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="w-14 h-14 bg-orange-600 rounded-sm flex items-center justify-center shrink-0">
            <HardHat className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-3">Our mission</h2>
            <p className="text-slate-700 text-base md:text-lg leading-relaxed">
              Make hiring a trusted contractor as easy, honest, and affordable as every other part of
              modern life — and make earning work as a skilled professional finally feel fair.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="max-w-4xl mx-auto px-4 md:px-8 py-14">
      <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">Our core commitments</h2>
      <p className="text-slate-600 mb-10">Three promises we'll keep — or go out of business trying.</p>
      <div className="grid md:grid-cols-3 gap-6">
        {VALUES.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="bg-white border border-slate-200 rounded-sm p-6 hover:border-orange-500 transition-colors"
            data-testid={`value-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="w-10 h-10 bg-orange-50 rounded-sm flex items-center justify-center mb-4">
              <Icon className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="font-black text-slate-900 text-lg mb-2">{title}</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Statement of Honesty & Transparency */}
    <section className="bg-slate-900 border-y-4 border-orange-600">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
        <div className="flex items-center gap-3 mb-5">
          <Shield className="w-6 h-6 text-orange-500" />
          <span className="text-orange-400 font-bold uppercase tracking-wider text-sm">Statement of Honesty &amp; Transparency</span>
        </div>
        <div className="space-y-4 text-slate-200 text-base md:text-lg leading-relaxed">
          <p>
            We believe the home-services industry runs on trust — and that trust is built one honest
            interaction at a time. That's why we commit, in writing, to the following:
          </p>
          <ul className="space-y-3 mt-4">
            {[
              'We verify every contractor\'s active state license and current general-liability + workers-comp insurance before their profile goes live.',
              'We do NOT sell, rent, or trade your personal information for third-party marketing use.',
              'We do NOT manipulate reviews, delete honest criticism, or offer contractors "reputation management" packages.',
              'We charge contractors only when a client actively selects them — never a lead-fee for a bid that didn\'t win.',
              'We disclose every fee, every charge, and every platform policy change in plain language, in advance, in writing.',
              'Our algorithms are explainable on request: if you want to know why a contractor was matched to your job, we\'ll tell you.',
              'If we ever get any of the above wrong, we\'ll say so publicly and fix it — fast.',
            ].map((text) => (
              <li key={text} className="flex gap-3 items-start">
                <CheckCircle className="w-5 h-5 text-orange-500 shrink-0 mt-1" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* Diversity & Inclusion */}
    <section className="max-w-4xl mx-auto px-4 md:px-8 py-14">
      <div className="flex items-center gap-3 mb-5">
        <Users className="w-6 h-6 text-orange-600" />
        <span className="text-orange-600 font-bold uppercase tracking-wider text-sm">Diversity &amp; Inclusion</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">
        The trades are bigger than any single community — and so are we.
      </h2>
      <div className="space-y-4 text-slate-700 text-base md:text-lg leading-relaxed">
        {DIVERSITY.map((paragraph) => (
          <p key={paragraph.slice(0, 40)}>{paragraph}</p>
        ))}
      </div>
    </section>

    {/* Operated by */}
    <section className="bg-slate-100 border-t border-slate-200">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Operated by</h3>
          <p className="text-slate-900 font-black text-xl mb-2">Retention Logic LLC</p>
          <p className="text-slate-600 text-sm leading-relaxed">
            ContractorsLab.com is a service of <strong>Retention Logic LLC</strong>, a limited
            liability company organized under the laws of the <strong>Commonwealth of Puerto
            Rico</strong>. All legal, commercial, and contractual obligations described on this
            site are obligations of Retention Logic LLC, governed by the laws of the Commonwealth of
            Puerto Rico and applicable U.S. federal laws. For press, partnerships, legal notices, or
            any official business, please use our{' '}
            <Link to="/contact" className="text-orange-600 font-semibold hover:underline">Contact page</Link>
            {' '}and select the appropriate department.
          </p>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-orange-600">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-white mb-1">Ready to see it in action?</h2>
          <p className="text-orange-50">Post your first job — free, no credit card.</p>
        </div>
        <Link
          to="/register?role=client"
          className="bg-white text-orange-600 font-bold uppercase tracking-wide px-8 py-4 rounded-sm hover:bg-slate-100 transition-colors flex items-center gap-2 whitespace-nowrap"
          data-testid="about-cta-post-job"
        >
          Post a Job <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  </div>
);

export default AboutPage;
